import EmptyMedicalCard from "../empty-medical-cards";
import allergiesImage from "~/assets/images/dashboard/allergies-medicals.svg";
import healthCondition from "~/assets/images/dashboard/health-condition.svg";
import { PatientMedicalHistory } from "../../api/types";
import { formatEncounterDate } from "@workspace/lib/date";

interface ConditionsAllergiesCardProps {
  history: PatientMedicalHistory | null;
}

const ConditionsAllergiesCard = ({ history }: ConditionsAllergiesCardProps) => {
  const getSeverityColor = (severity: string) => {
    if (severity === 'Severe') return 'bg-red-500';
    if (severity === 'Moderate') return 'bg-yellow-500';
    return 'bg-green-500';
  };

  // Define fields that should be displayed for allergies
  const allergyFields = [
    { key: 'severity', label: 'Severity' },
    { key: 'causes', label: 'Causes', isArray: true },
    { key: 'reaction', label: 'Symptoms' },
    { key: 'notes', label: 'Notes' },
    { key: 'status', label: 'Status'},
    { key: 'onset_date', label: 'Diagnosed'}
  ];

  // Updated to match your actual data structure
  const conditionFields = [
    { key: 'date_diagnosed', label: 'Diagnosed', isDate: true }
  ];

  // Custom function to handle medication display for conditions
  const renderMedication = (condition: any) => {
    const medications = [];
    
    if (condition.on_insulin) {
      medications.push('Insulin');
    }
    
    if (condition.other_medication && condition.other_medication !== 'null' && condition.other_medication.trim() !== '') {
      medications.push(condition.other_medication);
    }
    
    return medications.length > 0 ? medications.join(', ') : null;
  };

  const renderFieldValue = (field: any, value: any) => {
    if (!value) return null;
    
    if (field.isDate) {
      return formatEncounterDate(value);
    }
    
    if (field.isArray && Array.isArray(value)) {
      return value.join(", ");
    }
    
    if (field.isCommaDelimited && typeof value === 'string') {
      return value.split(',').map((item: string, idx: number) => (
        <div key={idx}>
          {item.trim()}
          <br />
        </div>
      ));
    }
    
    return value;
  };

  return (
    <div className="rounded-lg border shadow w-full bg-white">
      <div className="p-4 border-b">
        <h2 className="text-lg font-medium">Conditions & allergies</h2>
      </div>
      
      {/* Allergies */}
      <div className="p-4 border-b bg-white">
        <h3 className="font-medium mb-2">Allergies</h3>
        <div className="space-y-3">
          {history?.allergies && history.allergies.length > 0 ? (
            history.allergies.map((allergy: any, index: number) => (
              <div key={index} className="flex flex-col bg-[#6E23C90D] border-l-4 border-l-[#6E23C9] py-3 px-5 rounded-r-[8px] items-start">
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-2 ${getSeverityColor(allergy.severity)}`}></div>
                  <span className="text-[#1A202C] font-medium">{allergy.name}</span>
                </div>
                
                {/* Dynamic field rendering */}
                <div className="mt-2 space-y-1">
                  {allergyFields.map((field) => {
                    const value = allergy[field.key];
                    if (!value) return null;
                    
                    return (
                      <div key={field.key} className="flex items-center gap-2 text-[14px]">
                        <div className="text-gray-500">{field.label}:</div>
                        <span>{renderFieldValue(field, value)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          ) : (
            <EmptyMedicalCard
              title="Allergies"
              image={allergiesImage}
              description=""
              altImg="allergies"
              message="This patient has no recorded allergies"
            />
          )}
        </div>
      </div>

      {/* Health Conditions */}
      <div className="p-4">
        <h3 className="font-medium mb-2">Health Conditions</h3>
        <div className="grid grid-cols-1 items-center gap-4">
          {history?.conditions && history.conditions.length > 0 ? (
            history.conditions.map((condition: any, index: number) => (
              <div key={index} className="flex justify-between items-center border-l-4 border-l-[#FA3131] bg-[#FFE3E380] rounded-r-[8px] p-4">
                <div className="flex flex-col items-start w-full">
                  <span className="text-[#2D2F35] font-medium text-[16px]">{condition.name}</span>
                  
                  {/* Dynamic field rendering */}
                  <div className="mt-2 space-y-1 w-full">
                    {/* Handle diabetes conditions specially */}
                    {condition.name?.toLowerCase().includes('diabetes') ? (
                      <>
                        {/* Status - always show for diabetes with default */}
                        <div className="flex items-start gap-2 text-[14px]">
                          <div className="text-gray-500 min-w-fit">Status:</div>
                          <span className="text-[12px]">
                            {condition.status || 'Patient is diabetic'}
                          </span>
                        </div>
                        
                        {/* HbA1c - only show if data exists */}
                        {condition.last_hba1c_value && (
                          <div className="flex items-start gap-2 text-[14px]">
                            <div className="text-gray-500 min-w-fit">HbA1c:</div>
                            <span className="text-[12px]">
                              {condition.last_hba1c_value}
                            </span>
                          </div>
                        )}
                        
                        {/* Medication - only show if data exists */}
                        {renderMedication(condition) && (
                          <div className="flex items-start gap-2 text-[14px]">
                            <div className="text-gray-500 min-w-fit">Medication:</div>
                            <span className="text-[12px]">
                              {renderMedication(condition)}
                            </span>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {/* For non-diabetes conditions, show status only if exists */}
                        {condition.status && (
                          <div className="flex items-start gap-2 text-[14px]">
                            <div className="text-gray-500 min-w-fit">Status:</div>
                            <span className="text-[12px]">
                              {condition.status}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                    
                    {/* Show other fields for all conditions */}
                    {conditionFields.map((field) => {
                      const value = condition[field.key];
                      if (!value) return null;
                      
                      return (
                        <div key={field.key} className="flex items-start gap-2 text-[14px]">
                          <div className="text-gray-500 min-w-fit">{field.label}:</div>
                          <span className="text-[12px]">
                            {renderFieldValue(field, value)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <EmptyMedicalCard
              title="Health Conditions"
              image={healthCondition}
              description=""
              altImg="Health Conditions"
              message="This patient has no recorded health conditions"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ConditionsAllergiesCard;